import { EditOutlined, FileTextOutlined } from "@ant-design/icons";
import type { IntlShape } from "@formatjs/intl";
import type { Dispatch } from "@reduxjs/toolkit";
import {
  Alert,
  Button,
  DatePicker,
  Flex,
  Form,
  Input,
  Modal,
  Skeleton,
  Typography,
} from "antd";
import dayjs, { type Dayjs } from "dayjs";
import { ActiveSessionsBanner } from "./ActiveSessionsBanner";
import { DelayBanner } from "./DelayBanner";
import { RatingPanel } from "./RatingPanel";
import { RejectionBanner } from "./RejectionBanner";
import type { HookAPI } from "antd/es/modal/useModal";
import { useState } from "react";
import { useSelector } from "react-redux";
import { actions as routerActions } from "redux-router5";
import { type State as RouterState } from "router5";
import { WOActionButtons } from "../../../common/components/WOActionButtons.tsx";
import { WOStatusIndicator } from "../../../common/components/WOStatusIndicator.tsx";
import { PageTitleContainer } from "../../../common/components/PageTitle.tsx";
import { ReportDoneBadge } from "../../../common/components/ReportDoneBadge.tsx";
import { ReportDoneToggle } from "../../../common/components/ReportDoneToggle.tsx";
import { routeNames } from "../../common/routes.ts";
import type {
  ModelFile,
  Services,
  TicketRetrieve,
} from "../../generated-axios-client/index.ts";
import { ticketActions } from "../../../features/ticket/ticketSlice.ts";
import type { RootState } from "../../store.ts";
import { TicketInformation } from "./TicketInformation";

export type TicketData = {
  ticket: TicketRetrieve;
  serviceObjects: Record<string, Services>;
  files: ModelFile[] | null;
  materialCatalog: Record<string, string>;
  sapCodeCatalog: Record<string, string | null>;
};

// Disable past hours/minutes when the picked day is today so the standby-until
// value is always strictly in the future — the backend rejects a past time.
const disabledPastStandbyTime = (current: Dayjs | null) => {
  if (!current || !current.isSame(dayjs(), "day")) return {};
  const now = dayjs();
  return {
    disabledHours: () => Array.from({ length: now.hour() }, (_, hour) => hour),
    disabledMinutes: (selectedHour: number) =>
      selectedHour === now.hour()
        ? Array.from({ length: now.minute() + 1 }, (_, minute) => minute)
        : [],
  };
};

export type TicketViewRenderProps = {
  intl: IntlShape;
  currentRoute: RouterState | null;
  ticketData: TicketData;
  modalApi: HookAPI | undefined;
  dispatch?: Dispatch;
};

type TicketViewRenderPageProps = TicketViewRenderProps & {
  dispatch: Dispatch;
};

export const TicketViewRender = (props: TicketViewRenderPageProps) => {
  const { intl, currentRoute, ticketData, modalApi, dispatch } = props;
  const { ticket } = ticketData;

  const [delayMessage, setDelayMessage] = useState<string | null>(null);
  const [standbyTo, setStandbyTo] = useState<Dayjs | null>(null);
  const [standbyFrom, setStandbyFrom] = useState<Dayjs | null>(null);
  const [adjustStandbyFrom, setAdjustStandbyFrom] = useState(false);
  const [rejectMessage, setRejectMessage] = useState<string | null>(null);

  // A future start schedules standby; leaving the default (now) applies it
  // immediately. The backend also rejects a start at or after the standby-until
  // time, so the same range is guarded here for a clean error instead of a 400.
  const scheduledStandbyFrom = adjustStandbyFrom ? standbyFrom : null;
  const standbyRangeInvalid =
    scheduledStandbyFrom !== null &&
    standbyTo !== null &&
    !scheduledStandbyFrom.isBefore(standbyTo);

  const timeSessions = useSelector((state: RootState) => state.ticket.timeSessions);
  // Any technician still checked in blocks approval. Sessions load just after the
  // ticket (null while loading), so stay optimistic and do not block on unloaded data;
  // Reject is intentionally left enabled — it reopens the WO rather than closing it.
  const anyActiveCheckIn = timeSessions?.some((session) => !session.ended_at) ?? false;

  const toggleReportDone = (value: boolean) => {
    dispatch(ticketActions.setReportDone({ id: ticket.id!, value }));
  };

  if (!ticketData) {
    return <Skeleton active />;
  }

  return (
    <PageTitleContainer
      title={ticket.service?.name ?? ""}
      subtitle={`#${ticket.ticket_number}`}
      extra={
        <Button
          icon={<EditOutlined />}
          onClick={() =>
            dispatch(routerActions.navigateTo(routeNames.TICKET_EDIT, { id: ticket.id }))
          }
        >
          {intl.formatMessage({ id: "ticket_edit_title" })}
        </Button>
      }
    >
      <Flex vertical gap={12}>
        {/* biome-ignore lint/a11y/useValidAriaRole: `role` here is WOStatusIndicator's own
            typed prop ("management" | "field"), not an ARIA attribute - it selects which
            status UI to render. Biome cannot tell a domain prop on a custom component from
            a DOM aria role. It reports this as FIXABLE, but that autofix would drop or
            rewrite the prop and break the work order view; never run --write on it. */}
        <WOStatusIndicator currentStatus={ticket.status} role="management" />
        <Flex gap={8} align="center" wrap="wrap">
          <ReportDoneBadge reportDone={ticket.report_done} />
          <ReportDoneToggle
            reportDone={ticket.report_done}
            loading={false}
            onToggle={toggleReportDone}
          />
        </Flex>
        {(ticket.status === "IN_REVIEW" || ticket.status === "DONE") &&
          ticket.summary_text && (
            <Alert
              type="info"
              showIcon
              icon={<FileTextOutlined />}
              message={intl.formatMessage({ id: "ticket_summary_callout_title" })}
              description={ticket.summary_text}
            />
          )}
        {/* biome-ignore lint/a11y/useValidAriaRole: `role` here is WOActionButtons' own
            typed prop ("management" | "field"), not an ARIA attribute - it is the key into
            WO_STATUS_TRANSITIONS[currentStatus]?.[role], so it decides which actions the
            user is offered. Biome cannot tell a domain prop on a custom component from a
            DOM aria role. It reports this as FIXABLE, but that autofix would drop or
            rewrite the prop and break the work order view; never run --write on it. */}
        <WOActionButtons
          ticketId={ticket.id!}
          currentStatus={ticket.status}
          role="management"
          dispatch={dispatch}
          onOpenDelayModal={() => {
            setStandbyTo(null);
            setDelayMessage("");
          }}
          onOpenRejectModal={() => setRejectMessage("")}
          blockedByActiveCheckIn={anyActiveCheckIn}
          activeCheckInTooltip={intl.formatMessage({
            id: "checkin_active_blocks_approve",
          })}
        />
        {(ticket.status === "DONE" || ticket.status === "IN_REVIEW") && (
          <RatingPanel ticket={ticket} dispatch={dispatch} intl={intl} />
        )}
        <DelayBanner
          ticketId={ticket.id!}
          status={ticket.status}
          standbyTo={ticket.standby_to}
          standbyFrom={ticket.standby_from}
        />
        <RejectionBanner
          ticketId={ticket.id!}
          status={ticket.status}
          resolution={ticket.resolution}
        />
        <ActiveSessionsBanner />
      </Flex>

      <TicketInformation
        currentRoute={currentRoute}
        intl={intl}
        ticketData={ticketData}
        modalApi={modalApi}
        dispatch={dispatch}
      />

      <Modal
        open={delayMessage !== null}
        title={intl.formatMessage({ id: "ticket_status_delay_modal_title" })}
        okText={intl.formatMessage({ id: "ticket_button_title_create" })}
        okButtonProps={{ disabled: !delayMessage?.trim() || standbyRangeInvalid }}
        onOk={() => {
          dispatch(
            ticketActions.delayWorkOrder({
              id: ticket.id!,
              message: delayMessage!,
              standby_to: standbyTo ? standbyTo.toISOString() : null,
              standby_from: scheduledStandbyFrom
                ? scheduledStandbyFrom.toISOString()
                : null,
            })
          );
          setStandbyTo(null);
          setStandbyFrom(null);
          setAdjustStandbyFrom(false);
          setDelayMessage(null);
        }}
        onCancel={() => {
          setStandbyTo(null);
          setStandbyFrom(null);
          setAdjustStandbyFrom(false);
          setDelayMessage(null);
        }}
      >
        <Form layout="vertical">
          <Form.Item
            label={intl.formatMessage({ id: "ticket_status_delay_modal_label" })}
          >
            <Input.TextArea
              rows={4}
              placeholder={intl.formatMessage({
                id: "ticket_status_delay_modal_placeholder",
              })}
              value={delayMessage ?? ""}
              onChange={(e) => setDelayMessage(e.target.value)}
            />
          </Form.Item>
          <Form.Item
            label={intl.formatMessage({
              id: "ticket_status_delay_modal_standby_from_label",
            })}
            validateStatus={standbyRangeInvalid ? "error" : undefined}
            help={intl.formatMessage({
              id: standbyRangeInvalid
                ? "ticket_status_delay_modal_standby_from_invalid"
                : "ticket_status_delay_modal_standby_from_hint",
            })}
          >
            {adjustStandbyFrom ? (
              <DatePicker
                style={{ width: "100%" }}
                showTime={{ format: "HH:mm" }}
                format="DD/MM/YYYY HH:mm"
                placeholder={intl.formatMessage({
                  id: "ticket_status_delay_modal_standby_from_placeholder",
                })}
                value={standbyFrom}
                onChange={(value) => setStandbyFrom(value)}
              />
            ) : (
              <Flex align="center" gap={8}>
                <Typography.Text>{dayjs().format("DD/MM/YYYY HH:mm")}</Typography.Text>
                <Typography.Link onClick={() => setAdjustStandbyFrom(true)}>
                  {intl.formatMessage({ id: "ticket_adjust_time" })}
                </Typography.Link>
              </Flex>
            )}
          </Form.Item>
          <Form.Item
            label={intl.formatMessage({
              id: "ticket_status_delay_modal_standby_until_label",
            })}
            help={intl.formatMessage({
              id: "ticket_status_delay_modal_standby_until_hint",
            })}
          >
            <DatePicker
              style={{ width: "100%" }}
              showTime={{ format: "HH:mm" }}
              format="DD/MM/YYYY HH:mm"
              minDate={dayjs()}
              disabledTime={disabledPastStandbyTime}
              placeholder={intl.formatMessage({
                id: "ticket_status_delay_modal_standby_until_placeholder",
              })}
              value={standbyTo}
              onChange={(value) => setStandbyTo(value)}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={rejectMessage !== null}
        title={intl.formatMessage({ id: "ticket_status_reject_modal_title" })}
        okText={intl.formatMessage({ id: "ticket_button_title_create" })}
        okButtonProps={{ danger: true }}
        onOk={() => {
          dispatch(
            ticketActions.rejectWorkOrder({
              id: ticket.id!,
              message: rejectMessage?.trim() ? rejectMessage : undefined,
            })
          );
          setRejectMessage(null);
        }}
        onCancel={() => setRejectMessage(null)}
      >
        <Form layout="vertical">
          <Form.Item
            label={intl.formatMessage({ id: "ticket_status_reject_modal_label" })}
          >
            <Input.TextArea
              rows={4}
              maxLength={240}
              showCount
              placeholder={intl.formatMessage({
                id: "ticket_status_reject_modal_placeholder",
              })}
              value={rejectMessage ?? ""}
              onChange={(e) => setRejectMessage(e.target.value)}
            />
          </Form.Item>
        </Form>
      </Modal>
    </PageTitleContainer>
  );
};
